// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Mock AI Oracle
// Simulates LLM-quality strategic insights so users see the real product
// experience even before Claude API is connected. Output shape is identical
// to real Claude output — swapping engines is zero-friction.
//
// Generates narratives across 3 required categories: Financial / Operational /
// Marketing, with context-aware synthesis (not rigid rules).
// ══════════════════════════════════════════════════════════════════════════════

export class MockAIOracle {
  /**
   * Main generator. Returns up to 8 insights ranked by severity × impact.
   */
  static generate(data) {
    const ctx = this.analyzeContext(data);
    const insights = [
      ...this.financialNarratives(ctx),
      ...this.operationalNarratives(ctx),
      ...this.marketingNarratives(ctx)
    ];

    const severityWeight = { critical: 3, warning: 2, info: 1 };
    return insights
      .sort((a, b) =>
        (severityWeight[b.severity] * (b.expected_impact || 1)) -
        (severityWeight[a.severity] * (a.expected_impact || 1))
      )
      .slice(0, 8);
  }

  /**
   * Context analyzer — extracts signals that the narrative generators consume.
   */
  static analyzeContext(data) {
    const {
      sales = [], employees = [], gaps = [], ingredients = [],
      marketPrices = [], products = [], branches = [], cancellations = []
    } = data || {};

    // Financial
    const totalRev30d = sales.slice(0, 30).reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0);
    const avgDailyRev = totalRev30d / Math.min(30, sales.length || 1);
    const criticalGaps = gaps.filter(g => g.severity === 'critical' || g.severity === 'high');
    const totalMonthlyLoss = gaps.reduce((s, g) => s + parseFloat(g.estimated_monetary_loss || 0), 0);

    // Operational
    const flaggedEmployees = employees.filter(e => e.is_flagged || parseFloat(e.honesty_score) < 70);
    const totalCancellations = employees.reduce((s, e) => s + (e.total_cancellations || 0), 0);

    // Products
    const lowMarginProducts = products.filter(p => parseFloat(p.profit_margin) < 65);
    const highMarginProducts = products.filter(p => parseFloat(p.profit_margin) >= 75);
    const avgMargin = products.length
      ? products.reduce((s, p) => s + parseFloat(p.profit_margin), 0) / products.length
      : 0;

    // Time-of-day
    const recent = sales.slice(0, 14);
    const morningRev = recent.reduce((s, d) => s + parseFloat(d.morning_revenue || 0), 0);
    const afternoonRev = recent.reduce((s, d) => s + parseFloat(d.afternoon_revenue || 0), 0);
    const eveningRev = recent.reduce((s, d) => s + parseFloat(d.evening_revenue || 0), 0);
    const totalByPeriod = morningRev + afternoonRev + eveningRev;
    const eveningShare = totalByPeriod > 0 ? eveningRev / totalByPeriod : 0;
    const morningShare = totalByPeriod > 0 ? morningRev / totalByPeriod : 0;

    // Week-over-week
    const recent7 = sales.slice(0, 7);
    const prev7 = sales.slice(7, 14);
    const recentAvg = recent7.length ? recent7.reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0) / recent7.length : 0;
    const prevAvg = prev7.length ? prev7.reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0) / prev7.length : 0;
    const wowChange = prevAvg > 0 ? ((recentAvg - prevAvg) / prevAvg) * 100 : 0;

    // Overpriced suppliers
    const overpriced = ingredients
      .map(ing => {
        const market = marketPrices.find(m => m.material_name_ar === ing.name_ar);
        if (!market) return null;
        const you = parseFloat(ing.purchase_price);
        const ref = parseFloat(market.market_price);
        const diffPct = ((you - ref) / ref) * 100;
        if (diffPct <= 10) return null;
        return {
          ingredient: ing, market, diffPct,
          monthlyOverspend: (you - ref) * parseFloat(ing.actual_consumption_monthly || 20)
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.monthlyOverspend - a.monthlyOverspend);

    // Day-of-week patterns
    const byDayOfWeek = [0,0,0,0,0,0,0];
    const countByDay = [0,0,0,0,0,0,0];
    sales.slice(0, 28).forEach(s => {
      if (!s.sale_date) return;
      const day = new Date(s.sale_date).getDay();
      byDayOfWeek[day] += parseFloat(s.total_revenue || 0);
      countByDay[day]++;
    });
    const avgByDay = byDayOfWeek.map((total, i) => countByDay[i] ? total / countByDay[i] : 0);
    const dayNames_ar = ['الأحد','الاثنين','الثلاثاء','الأربعاء','الخميس','الجمعة','السبت'];
    const dayNames_en = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

    const validDays = avgByDay.map((v, i) => ({ v, i })).filter(x => x.v > 0);
    const weakestDay = validDays.length ? [...validDays].sort((a, b) => a.v - b.v)[0] : null;
    const strongestDay = validDays.length ? [...validDays].sort((a, b) => b.v - a.v)[0] : null;

    return {
      totalRev30d, avgDailyRev, criticalGaps, totalMonthlyLoss,
      flaggedEmployees, totalCancellations,
      lowMarginProducts, highMarginProducts, avgMargin,
      eveningShare, morningShare, eveningRev, morningRev, afternoonRev,
      wowChange, recentAvg, prevAvg,
      overpriced,
      weakestDay, strongestDay, dayNames_ar, dayNames_en, avgByDay,
      sales, employees, products, branches, ingredients
    };
  }

  // ═════════════ FINANCIAL ══════════════════════════════════════════════
  static financialNarratives(ctx) {
    const out = [];

    if (ctx.criticalGaps.length > 0 && ctx.totalMonthlyLoss > 500) {
      const gap = ctx.criticalGaps[0];
      out.push({
        id: `mockai_fin_leak_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        insight_type: 'financial',
        severity: ctx.totalMonthlyLoss > 2000 ? 'critical' : 'warning',
        title_ar: `نزيف مالي نشط بقيمة ${Math.round(ctx.totalMonthlyLoss).toLocaleString()} ر.س شهرياً`,
        title_en: `Active financial leak of SAR ${Math.round(ctx.totalMonthlyLoss).toLocaleString()}/month`,
        description_ar: `بتحليل ${ctx.criticalGaps.length} فجوة حرجة في بياناتك، اكتشفتُ نمطاً منهجياً للخسائر يتطلب تدخلاً فورياً.`,
        description_en: `Analysis of ${ctx.criticalGaps.length} critical gaps reveals a systematic loss pattern requiring immediate attention.`,
        why_ar: `أبرز فجوة: "${gap.title_ar || gap.description_ar}".\n\nالخسارة المتراكمة تمثل ${((ctx.totalMonthlyLoss / Math.max(ctx.totalRev30d, 1)) * 100).toFixed(1)}% من إيراداتك الشهرية — فوق الحد الصحي (أقل من 1.5%).\n\nالنمط يشير إلى عملية مستمرة، لا حدث منفرد. إذا استمرت 12 شهراً، ستخسر ${(ctx.totalMonthlyLoss * 12).toLocaleString()} ر.س سنوياً.`,
        why_en: `Top gap: "${gap.title_en || gap.description_en}".\n\nCumulative loss = ${((ctx.totalMonthlyLoss / Math.max(ctx.totalRev30d, 1)) * 100).toFixed(1)}% of monthly revenue — above the healthy 1.5% threshold.\n\nThis is a systemic issue, not isolated. Unaddressed for 12 months: SAR ${(ctx.totalMonthlyLoss * 12).toLocaleString()} annual loss.`,
        how_ar: `خطة معالجة 30 يوم:\n1. أسبوع 1: تدقيق يومي لوصفات المنتجات vs الاستهلاك الفعلي\n2. أسبوع 2: تركيب كاميرات المطبخ + تتبع يومي للمخزون\n3. أسبوع 3: تدريب الطاقم على إجراءات التخزين الجديدة\n4. أسبوع 4: مراجعة — يُتوقع تخفيض 70-85% من الفجوات`,
        how_en: `30-day remediation:\n1. Week 1: Daily recipe audit vs actual consumption\n2. Week 2: Kitchen cameras + daily inventory tracking\n3. Week 3: Staff retraining on storage procedures\n4. Week 4: Review — expect 70-85% gap reduction`,
        expected_impact: Math.round(ctx.totalMonthlyLoss * 0.75),
        is_llm_generated: true,
        is_mock: true
      });
    }

    if (ctx.lowMarginProducts.length >= 3 && ctx.avgMargin < 70) {
      const topProblem = [...ctx.lowMarginProducts].sort((a, b) =>
        parseFloat(a.profit_margin) - parseFloat(b.profit_margin))[0];
      out.push({
        id: `mockai_fin_margin_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        insight_type: 'pricing',
        severity: 'warning',
        title_ar: 'تآكل ممنهج في هوامش الربح عبر القائمة',
        title_en: 'Systematic margin erosion across menu',
        description_ar: `${ctx.lowMarginProducts.length} منتج تحت خط الهامش الصحي (65%). متوسط الصناعة 72%.`,
        description_en: `${ctx.lowMarginProducts.length} products below healthy margin (65%). Industry benchmark 72%.`,
        why_ar: `الأسوأ: "${topProblem.name_ar}" بهامش ${parseFloat(topProblem.profit_margin).toFixed(1)}% فقط — تربح ${(parseFloat(topProblem.selling_price) - parseFloat(topProblem.cost_price)).toFixed(2)} ر.س لكل طلب.\n\nالسبب الأرجح: ارتفاع تكاليف المواد دون تعديل الأسعار. الفرق بين هامشك (${ctx.avgMargin.toFixed(1)}%) ومعيار السوق يعني ~${(ctx.totalRev30d * (72 - ctx.avgMargin) / 100).toLocaleString()} ر.س شهرياً متروكة على الطاولة.`,
        why_en: `Worst: "${topProblem.name_en}" at ${parseFloat(topProblem.profit_margin).toFixed(1)}% — SAR ${(parseFloat(topProblem.selling_price) - parseFloat(topProblem.cost_price)).toFixed(2)} profit per order.\n\nLikely cause: raw material cost rises without price adjustments. Gap vs benchmark = ~SAR ${(ctx.totalRev30d * (72 - ctx.avgMargin) / 100).toLocaleString()}/month left on table.`,
        how_ar: `3 تكتيكات تصاعدية:\n1. سريع: رفع الأسعار 2-3 ر.س (تأثير طلب -5%، ربح صافٍ +18%)\n2. متوسط: التفاوض مع أعلى 3 موردين استهلاكاً\n3. استراتيجي: تقليل الحصة 8-12% (لا يلاحظه العملاء تحت 15%)`,
        how_en: `3 escalating tactics:\n1. Quick: Raise prices SAR 2-3 (demand impact -5%, net +18%)\n2. Medium: Renegotiate top-3 highest-consumption suppliers\n3. Strategic: Reduce portion 8-12% (imperceptible under 15%)`,
        expected_impact: Math.round(ctx.totalRev30d * 0.05),
        is_llm_generated: true,
        is_mock: true
      });
    }

    if (ctx.wowChange < -8) {
      out.push({
        id: `mockai_fin_drop_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        insight_type: 'financial',
        severity: ctx.wowChange < -15 ? 'critical' : 'warning',
        title_ar: `انكماش الإيرادات ${Math.abs(ctx.wowChange).toFixed(1)}% في الأسبوع الأخير`,
        title_en: `${Math.abs(ctx.wowChange).toFixed(1)}% revenue contraction this week`,
        description_ar: 'مقارنة الأسبوعين تُظهر تراجعاً يستحق التحقيق الفوري.',
        description_en: 'Week-over-week comparison shows a decline worth investigating immediately.',
        why_ar: `الأسبوع الحالي ${Math.round(ctx.recentAvg * 7).toLocaleString()} ر.س vs السابق ${Math.round(ctx.prevAvg * 7).toLocaleString()} ر.س. الخسارة الفورية: ${Math.round((ctx.prevAvg - ctx.recentAvg) * 7).toLocaleString()} ر.س.\n\nأسباب محتملة (مرتبة):\n• تراجع جودة منتج/خدمة (راجع التقييمات)\n• منافس جديد قريب\n• تحوّل موسمي\n• مشكلة موظفين/تشغيل`,
        why_en: `This week SAR ${Math.round(ctx.recentAvg * 7).toLocaleString()} vs last SAR ${Math.round(ctx.prevAvg * 7).toLocaleString()}. Direct loss: SAR ${Math.round((ctx.prevAvg - ctx.recentAvg) * 7).toLocaleString()}.\n\nLikely (ranked):\n• Product/service quality drop (check reviews)\n• New nearby competitor\n• Seasonal shift\n• Staff/ops issue`,
        how_ar: `تحقيق 48 ساعة:\n1. راجع تقييمات Google/Talabat آخر 14 يوم\n2. قابل 3 عملاء متكررين مباشرة\n3. افحص مبيعات ساعة-بساعة لاكتشاف وقت الانخفاض\n4. راجع جدول الموظفين — هل تغيّر شيء؟`,
        how_en: `48-hour investigation:\n1. Google/Talabat reviews last 14 days\n2. Meet 3 regular customers directly\n3. Hour-by-hour sales to pinpoint decline timing\n4. Audit staff schedule — any changes?`,
        expected_impact: Math.round((ctx.prevAvg - ctx.recentAvg) * 30),
        is_llm_generated: true,
        is_mock: true
      });
    }

    return out;
  }

  // ═════════════ OPERATIONAL ════════════════════════════════════════════
  static operationalNarratives(ctx) {
    const out = [];

    if (ctx.flaggedEmployees.length > 0) {
      const worst = [...ctx.flaggedEmployees].sort((a, b) =>
        parseFloat(a.honesty_score) - parseFloat(b.honesty_score))[0];
      const totalFlaggedCancels = ctx.flaggedEmployees.reduce(
        (s, e) => s + (e.total_cancellations || 0), 0);
      out.push({
        id: `mockai_ops_eff_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        insight_type: 'fraud',
        severity: ctx.flaggedEmployees.length >= 3 ? 'critical' : 'warning',
        title_ar: `${ctx.flaggedEmployees.length} موظف في منطقة الخطر التشغيلي`,
        title_en: `${ctx.flaggedEmployees.length} employees in operational risk zone`,
        description_ar: 'تحليل أنماط الإلغاء يكشف انحرافات إحصائية مقلقة لدى مجموعة محددة.',
        description_en: 'Cancellation pattern analysis reveals statistical anomalies in a specific group.',
        why_ar: `الأكثر خطورة: "${worst.name_ar}" بدرجة نزاهة ${parseFloat(worst.honesty_score).toFixed(0)}% و${worst.total_cancellations} إلغاء (المتوسط الصحي أقل من 30).\n\nإجمالي الإلغاءات المشبوهة: ${totalFlaggedCancels}. بـ 65 ر.س/عملية، الخسارة المحتملة: ${(totalFlaggedCancels * 65).toLocaleString()} ر.س.\n\nمهم: لا نجزم بسوء نية — قد تكون فجوات تدريب. لكن المعدل يتطلب تحقيقاً.`,
        why_en: `Highest risk: "${worst.name_en}" at ${parseFloat(worst.honesty_score).toFixed(0)}% honesty, ${worst.total_cancellations} voids (healthy under 30).\n\nTotal suspicious voids: ${totalFlaggedCancels}. At SAR 65 avg, potential loss: SAR ${(totalFlaggedCancels * 65).toLocaleString()}.\n\nImportant: not asserting malice — could be training gaps. But rate warrants investigation.`,
        how_ar: `بروتوكول "احتواء بدون اتهام" (أسبوعين):\n\nأسبوع 1 (ملاحظة):\n• رمز مدير إلزامي لكل إلغاء > 50 ر.س\n• مراجعة كاميرات POS يومياً\n• تدريب جماعي على "أخطاء المحاسبة الشائعة"\n\nأسبوع 2 (قياس):\n• هل انخفض المعدل 40%+؟ إذن المشكلة إجرائية\n• إذا استمر، قابل الموظفين فردياً بدليل محدد`,
        how_en: `"Contain without accusing" protocol (2 weeks):\n\nWeek 1 (observe):\n• Manager code mandatory for any void > SAR 50\n• Daily POS camera review\n• Group training on "common accounting errors"\n\nWeek 2 (measure):\n• Rate dropped 40%+? Then procedural\n• If unchanged, meet individually with specific evidence`,
        expected_impact: Math.round(totalFlaggedCancels * 65 * 0.6),
        is_llm_generated: true,
        is_mock: true
      });
    }

    if (ctx.eveningShare < 0.15 && ctx.eveningShare > 0 && ctx.branches.length > 0) {
      out.push({
        id: `mockai_ops_staff_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        insight_type: 'staffing',
        severity: 'warning',
        title_ar: 'الفترة المسائية تستنزف رواتب دون إيرادات مقابلة',
        title_en: 'Evening hours drain wages without proportional revenue',
        description_ar: `الفترة المسائية تمثل ${(ctx.eveningShare * 100).toFixed(1)}% فقط من الإيراد — أقل من عتبة الكفاءة (15%).`,
        description_en: `Evening captures only ${(ctx.eveningShare * 100).toFixed(1)}% — below efficiency threshold (15%).`,
        why_ar: `بتحليل آخر 14 يوم: مسائي ${Math.round(ctx.eveningRev).toLocaleString()} ر.س vs صباحي ${Math.round(ctx.morningRev).toLocaleString()} vs عصري ${Math.round(ctx.afternoonRev).toLocaleString()}.\n\nالمسألة: 3-4 موظفين مساءً بنفس تكلفة الصباح = ~100 ر.س عمالة/ساعة مقابل ~40 ر.س إيراد. وردية خاسرة 60%.`,
        why_en: `Last 14 days: evening SAR ${Math.round(ctx.eveningRev).toLocaleString()} vs morning SAR ${Math.round(ctx.morningRev).toLocaleString()} vs afternoon SAR ${Math.round(ctx.afternoonRev).toLocaleString()}.\n\nThe issue: 3-4 staff evenings at same cost as morning = ~SAR 100/hr labor vs ~SAR 40/hr revenue. 60% loss shift.`,
        how_ar: `3 خيارات:\n1. التقليص: خفض الطاقم المسائي من 4 إلى 2 (وفر 1,800 ر.س/فرع/شهر)\n2. الإغلاق المبكر: آخر ساعة إذا < 20% إيراد الذروة\n3. التفعيل: Happy Hour مسائي بخصم 20%`,
        how_en: `3 options:\n1. Reduce: cut evening staff 4→2 (SAR 1,800/branch/month savings)\n2. Close early: if last hour < 20% of peak\n3. Activate: evening Happy Hour 20% off`,
        expected_impact: 1800 * (ctx.branches.length || 1),
        is_llm_generated: true,
        is_mock: true
      });
    }

    if (ctx.overpriced.length > 0) {
      const total = ctx.overpriced.reduce((s, x) => s + x.monthlyOverspend, 0);
      const top = ctx.overpriced[0];
      out.push({
        id: `mockai_ops_supply_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        insight_type: 'opportunity',
        severity: total > 1000 ? 'warning' : 'info',
        title_ar: `${ctx.overpriced.length} مورد يفرض عليك أسعاراً فوق السوق`,
        title_en: `${ctx.overpriced.length} suppliers charging above market`,
        description_ar: `مقارنة أسعارك بمتوسط الرياض تكشف فرصة توفير ~${Math.round(total).toLocaleString()} ر.س شهرياً.`,
        description_en: `Your prices vs Riyadh market reveal ~SAR ${Math.round(total).toLocaleString()}/month savings opportunity.`,
        why_ar: `الأسوأ: "${top.ingredient.name_ar}" — تدفع ${parseFloat(top.ingredient.purchase_price).toFixed(2)} والسوق ${parseFloat(top.market.market_price).toFixed(2)} (فرق ${top.diffPct.toFixed(1)}%).\n\nبالاستهلاك ${parseFloat(top.ingredient.actual_consumption_monthly || 20).toFixed(0)} ${top.ingredient.unit}/شهر، وفر هذه المادة وحدها: ${Math.round(top.monthlyOverspend).toLocaleString()} ر.س/شهر = ${Math.round(top.monthlyOverspend * 12).toLocaleString()} ر.س/سنة.`,
        why_en: `Worst: "${top.ingredient.name_en}" — paying SAR ${parseFloat(top.ingredient.purchase_price).toFixed(2)} vs market SAR ${parseFloat(top.market.market_price).toFixed(2)} (${top.diffPct.toFixed(1)}% gap).\n\nAt ${parseFloat(top.ingredient.actual_consumption_monthly || 20).toFixed(0)} ${top.ingredient.unit}/month, this single item = SAR ${Math.round(top.monthlyOverspend).toLocaleString()}/month or SAR ${Math.round(top.monthlyOverspend * 12).toLocaleString()}/year savings.`,
        how_ar: `تكتيك التفاوض (جرّبه هذا الأسبوع):\n1. اطبع تقرير مقارنة الأسعار من سيلدور\n2. اتصل بموردك واذكر السعر السائد\n3. اطلب مطابقة أو خصم 80% من الفرق\n4. إذا رفض، مهلة انتقال 30 يوم\n5. لم يتحرك؟ افتح طلب عروض لـ 3 بدلاء`,
        how_en: `Negotiation tactic (try this week):\n1. Print Seldor price comparison\n2. Call supplier, cite market rate\n3. Request match or 80% of gap as discount\n4. Refused? Set 30-day transition deadline\n5. Still no movement? RFQ to 3 alternatives`,
        expected_impact: Math.round(total),
        is_llm_generated: true,
        is_mock: true
      });
    }

    return out;
  }

  // ═════════════ MARKETING ══════════════════════════════════════════════
  static marketingNarratives(ctx) {
    const out = [];

    if (ctx.weakestDay && ctx.strongestDay &&
        ctx.avgByDay[ctx.weakestDay.i] < ctx.avgByDay[ctx.strongestDay.i] * 0.6) {
      const weakName_ar = ctx.dayNames_ar[ctx.weakestDay.i];
      const weakName_en = ctx.dayNames_en[ctx.weakestDay.i];
      const strongName_ar = ctx.dayNames_ar[ctx.strongestDay.i];
      const strongName_en = ctx.dayNames_en[ctx.strongestDay.i];
      const gap = ctx.strongestDay.v - ctx.weakestDay.v;
      const potentialMonthly = gap * 4;
      out.push({
        id: `mockai_mkt_day_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        insight_type: 'marketing',
        severity: 'info',
        title_ar: `يوم ${weakName_ar} هو أضعف نقطة في أسبوعك`,
        title_en: `${weakName_en} is your weakest revenue day`,
        description_ar: `فرصة نمو واضحة: إيرادات ${weakName_ar} أقل بـ ${((1 - ctx.weakestDay.v / ctx.strongestDay.v) * 100).toFixed(0)}% من ${strongName_ar}.`,
        description_en: `Clear growth lever: ${weakName_en} revenue is ${((1 - ctx.weakestDay.v / ctx.strongestDay.v) * 100).toFixed(0)}% below ${strongName_en}.`,
        why_ar: `بتحليل آخر 4 أسابيع:\n• ${strongName_ar}: ${Math.round(ctx.strongestDay.v).toLocaleString()} ر.س (الأقوى)\n• ${weakName_ar}: ${Math.round(ctx.weakestDay.v).toLocaleString()} ر.س (الأضعف)\n\nالفرق اليومي: ${Math.round(gap).toLocaleString()} ر.س. رفع ${weakName_ar} إلى 80% من الذروة = ~${Math.round(potentialMonthly * 0.6).toLocaleString()} ر.س إضافية شهرياً.\n\nنمط شائع: العملاء يرتبطون بعاداتهم. اربط ${weakName_ar} بعرض مميز لـ 6 أسابيع لبناء عادة جديدة.`,
        why_en: `Last 4 weeks:\n• ${strongName_en}: SAR ${Math.round(ctx.strongestDay.v).toLocaleString()} (strongest)\n• ${weakName_en}: SAR ${Math.round(ctx.weakestDay.v).toLocaleString()} (weakest)\n\nDaily gap: SAR ${Math.round(gap).toLocaleString()}. Lifting ${weakName_en} to 80% of peak = ~SAR ${Math.round(potentialMonthly * 0.6).toLocaleString()} extra/month.\n\nCustomers anchor on habits. Tag ${weakName_en} with a distinctive offer for 6 weeks, a new habit forms.`,
        how_ar: `حملة "${weakName_ar} المميز" (6 أسابيع):\nأسبوع 1-2: "صباح ${weakName_ar}" — قهوة مجانية مع أي فطور\nأسبوع 3-4: "عائلة ${weakName_ar}" — خصم 15% للمجموعات 3+\nأسبوع 5-6: "${weakName_ar} الذهبي" — نقاط ولاء مضاعفة\n\nقس بعد 6 أسابيع. احتفظ بالأنجح كعلامة دائمة.`,
        how_en: `"${weakName_en} Special" campaign (6 weeks):\nWeek 1-2: "${weakName_en} Morning" — free coffee with breakfast\nWeek 3-4: "Family ${weakName_en}" — 15% off groups of 3+\nWeek 5-6: "Golden ${weakName_en}" — 2x loyalty points\n\nMeasure after 6 weeks. Keep winner as permanent brand.`,
        expected_impact: Math.round(potentialMonthly * 0.6),
        is_llm_generated: true,
        is_mock: true
      });
    }

    if (ctx.highMarginProducts.length > 0 && ctx.products.length >= 5) {
      const hero = ctx.highMarginProducts[0];
      out.push({
        id: `mockai_mkt_hero_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        insight_type: 'marketing',
        severity: 'info',
        title_ar: `"${hero.name_ar}" بطلك غير المستغل`,
        title_en: `"${hero.name_en}" is your underutilized hero product`,
        description_ar: `بهامش ربح ${parseFloat(hero.profit_margin).toFixed(1)}%، هذا المنتج يجب أن يكون واجهة قائمتك.`,
        description_en: `At ${parseFloat(hero.profit_margin).toFixed(1)}% margin, this belongs on your menu's front page.`,
        why_ar: `علم السلوك: 60% من القرارات بصرية. إذا "${hero.name_ar}" في الأسفل أو غير مميز، أنت تُضيّع بطل الربح.\n\nرياضياً: كل +10 طلبات شهرياً = ${((parseFloat(hero.selling_price) - parseFloat(hero.cost_price)) * 10).toFixed(0)} ر.س صافي. الترويج الذكي قد يرفع 30-50%.`,
        why_en: `Consumer behavior: 60% of decisions are visual. If "${hero.name_en}" sits at bottom or unhighlighted, you're wasting profit.\n\nMath: each +10 orders/month = SAR ${((parseFloat(hero.selling_price) - parseFloat(hero.cost_price)) * 10).toFixed(0)} net. Smart promotion can lift 30-50%.`,
        how_ar: `تكتيك "تسليط الضوء":\n1. اجعله البند الأول (ورقي + إلكتروني)\n2. أضف صورة عالية الجودة\n3. وصف حسي ("محضّر بعناية"، "طازج يومياً")\n4. اقتراح الباريستا: "الأكثر طلباً"\n5. Combo مع منتج متوسط الهامش`,
        how_en: `"Spotlight the hero" tactic:\n1. Make it item #1 (print + digital)\n2. Add high-quality photo\n3. Sensory description ("carefully crafted", "fresh daily")\n4. Barista prompt: "Our most popular"\n5. Bundle with mid-margin item`,
        expected_impact: Math.round((parseFloat(hero.selling_price) - parseFloat(hero.cost_price)) * 40),
        is_llm_generated: true,
        is_mock: true
      });
    }

    if (ctx.morningShare > 0 && ctx.morningShare < 0.25 && ctx.afternoonRev > ctx.morningRev * 1.5) {
      out.push({
        id: `mockai_mkt_morning_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        insight_type: 'marketing',
        severity: 'info',
        title_ar: 'الفترة الصباحية فرصة ذهبية غير مكتشفة',
        title_en: 'Morning hours are untapped gold',
        description_ar: `الصباح يمثل ${(ctx.morningShare * 100).toFixed(1)}% فقط — المتوسط الصحي 30-35%.`,
        description_en: `Morning = ${(ctx.morningShare * 100).toFixed(1)}% — healthy cafe average 30-35%.`,
        why_ar: `منطقياً: نفس الإيجار + نفس الطاقم + نفس الآلات صباحاً وعصراً. لكن إيرادك الصباحي (${Math.round(ctx.morningRev).toLocaleString()} ر.س) أقل بكثير من العصر (${Math.round(ctx.afternoonRev).toLocaleString()} ر.س).\n\nكل ريال صباحي إضافي = ~100% ربح صافٍ (التكاليف الثابتة مغطاة أصلاً).`,
        why_en: `Same rent + same staff + same equipment morning and afternoon. Yet morning (SAR ${Math.round(ctx.morningRev).toLocaleString()}) lags afternoon (SAR ${Math.round(ctx.afternoonRev).toLocaleString()}).\n\nEvery extra morning SAR is ~100% profit (fixed costs already covered).`,
        how_ar: `3 حركات:\n1. "طقم الصباح" — قهوة + فطور بسعر ثابت 22 ر.س قبل 10ص\n2. شراكة مع 3 مكاتب قريبة — عرض خاص\n3. اشتراك القهوة — 99 ر.س/شهر = 20 قهوة صباحية`,
        how_en: `3 moves:\n1. "Morning Combo" — coffee + breakfast SAR 22 before 10 AM\n2. Partnership with 3 nearby offices — exclusive deal\n3. "Coffee Subscription" — SAR 99/month = 20 morning coffees`,
        expected_impact: Math.round(ctx.morningRev * 0.5),
        is_llm_generated: true,
        is_mock: true
      });
    }

    return out;
  }
}

export default MockAIOracle;
